<template>
  <div class="theme-page-container">
    <PremiumPage title="Theme Settings" subtitle="Customize the look and feel of your admin portal" showBack>
      <div class="pa-6">
      <!-- ══════════════════════════════════════
             LIVE PREVIEW
        ══════════════════════════════════════ -->
      <div class="preview-section mb-3">

        <!-- Section header -->
        <div class="preview-section-header">
          <div>
            <div class="preview-section-title">Live Preview</div>
            <div class="preview-section-sub">See how your colors look before saving</div>
          </div>
        </div>

        <!-- Preview shell: exact replica of your portal layout -->
        <div class="lp-shell">

          <!-- ── Sidebar (matches your portal exactly) ── -->
          <div class="lp-sidebar">

            <!-- Logo bar — same height/style as your portal -->
            <div class="lp-logo-bar" :style="`background:${theme_primary_color || primaryColor}`">
              <div class="lp-logo-avatar">
                <template v-if="user && user.logo_url && !imageError">
                  <img :src="API_BASE_URL + '/uploads/' + user.logo_url" :alt="user.name"
                    style="width: 100%; height: 100%; border-radius: 50%; object-fit: contain;"
                    @error="imageError = true" />
                </template>
                <template v-else>
                  {{ user && user.name ? user.name.charAt(0).toUpperCase() : "S" }}
                </template>
              </div>
              <div>
                <div class="lp-logo-name">{{ user && user.name ? user.name : "Your Society" }}</div>
                <div class="lp-logo-sub">Society Management</div>
              </div>
            </div>

            <!-- Nav items -->
            <div class="lp-nav-area">

              <div class="lp-nav-section-label">OVERVIEW</div>
              <div class="lp-nav-row">
                <v-icon size="15" color="rgba(0,0,0,0.45)">mdi-grid-large</v-icon>
                <span>Dashboard</span>
              </div>

              <div class="lp-nav-section-label" style="margin-top:8px">DAILY OPERATIONS</div>
              <div class="lp-nav-row">
                <v-icon size="15" color="rgba(0,0,0,0.45)">mdi-account-arrow-right-outline</v-icon>
                <span>Visitors</span>
              </div>

              <div class="lp-nav-row">
                <v-icon size="15" color="rgba(0,0,0,0.45)">mdi-face-agent</v-icon>
                <span>Help Desk</span>
              </div>

              <div class="lp-nav-section-label" style="margin-top:8px">MEMBERS</div>
              <div class="lp-nav-row">
                <v-icon size="15" color="rgba(0,0,0,0.45)">mdi-account-clock-outline</v-icon>
                <span>Member Requests</span>
              </div>
              <div class="lp-nav-row lp-nav-active"
                :style="`background:${(theme_primary_color || primaryColor)}15; color:${theme_primary_color || primaryColor}; border-left:3px solid ${theme_primary_color || primaryColor}`">
                <v-icon size="15" :color="theme_primary_color || primaryColor">mdi-account-multiple-outline</v-icon>
                <span style="font-weight:600">Members</span>
              </div>


              <!-- <div class="lp-nav-section-label" style="margin-top:8px">FINANCE</div>
              <div class="lp-nav-row">
                <v-icon size="15" color="rgba(0,0,0,0.45)">mdi-bank-outline</v-icon>
                <span>Bills</span>
              </div>
              <div class="lp-nav-row">
                <v-icon size="15" color="rgba(0,0,0,0.45)">mdi-card-text-outline</v-icon>
                <span>Expenses</span>
              </div> -->
            </div>
          </div>

          <!-- ── Right side: topbar + content ── -->
          <div class="lp-right">

            <!-- Topbar — matches your portal's blue bar exactly -->
            <div class="lp-topbar" :style="`background:${theme_primary_color || primaryColor}`">
              <div class="lp-topbar-title"></div>
              <div class="lp-topbar-actions">
                <div class="lp-topbar-btn"><v-icon size="15" color="white">mdi-chat-outline</v-icon></div>
                <div class="lp-topbar-btn" style="position:relative">
                  <v-icon size="15" color="white">mdi-bell-outline</v-icon>
                  <span class="lp-bell-dot"></span>
                </div>
                <div class="lp-topbar-avatar" :style="`background:${theme_button_color || buttonColor}`">
                  <template v-if="user && user.logo_url && !imageError">
                    <img :src="API_BASE_URL + '/uploads/' + user.logo_url" :alt="user.name"
                      style="width: 100%; height: 100%; border-radius: 50%; object-fit: contain;"
                      @error="imageError = true" />
                  </template>
                  <template v-else>
                    {{ user && user.name ? user.name.charAt(0).toUpperCase() : "S" }}
                  </template>
                </div>
              </div>
            </div>

            <!-- Page content -->
            <div class="lp-page-body">

              <!-- Page title row (matches your portal's "Visitors / Members" heading) -->
              <div class="lp-page-header">
                <div>
                  <div class="lp-page-title">Members</div>
                  <div class="lp-page-sub">Manage all resident members of the society</div>
                </div>
                <div class="lp-page-actions">
                  <div class="lp-search-box">
                    <v-icon size="13" color="#9ca3af">mdi-magnify</v-icon>
                    <span class="lp-search-placeholder">Search...</span>
                  </div>
                  <div class="lp-filter-btn">
                    <v-icon size="12" color="#374151">mdi-filter-outline</v-icon>
                    <span>Filter by Block</span>
                    <v-icon size="11" color="#374151">mdi-chevron-down</v-icon>
                  </div>
                  <div class="lp-filter-btn">
                    <span>Unit</span>
                    <v-icon size="11" color="#374151">mdi-chevron-down</v-icon>
                  </div>
                  <!-- Download btn -->
                  <div class="lp-icon-btn" :style="`background:${theme_button_color || buttonColor}`">
                    <v-icon size="13" color="white">mdi-download</v-icon>
                  </div>
                  <!-- Add btn -->
                  <div class="lp-add-btn" :style="`background:${theme_button_color || buttonColor}`">
                    <v-icon size="12" color="white">mdi-plus</v-icon>
                    <span>Add</span>
                  </div>
                </div>
              </div>

              <!-- Table — exact replica of your Members table -->
              <div class="lp-table-card">
                <!-- Table header -->
                <div class="lp-table-head">
                  <div class="lp-th lp-col-no" style="text-align:center">No</div>
                  <div class="lp-th lp-col-name">Name</div>
                  <div class="lp-th lp-col-house">Unit</div>
                  <div class="lp-th lp-col-contact">Contact No</div>
                  <div class="lp-th lp-col-active">Last Active</div>
                  <div class="lp-th lp-col-actions" style="text-align:right">Actions</div>
                </div>

                <!-- Table rows (Skeleton shimmers matching portal loading state) -->
                <div v-for="i in 2" :key="i" class="lp-table-row">
                  <div class="lp-td lp-col-no">
                    <div class="lp-shimmer" style="width: 22px; height: 18px; border-radius: 5px;"></div>
                  </div>
                  <div class="lp-td lp-col-name">
                    <div class="lp-shimmer" style="width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;">
                    </div>
                    <div class="lp-shimmer"
                      :style="`width: ${i === 1 ? 80 : i === 2 ? 60 : i === 3 ? 90 : 70}px; height: 12px; border-radius: 4px;`">
                    </div>
                  </div>
                  <div class="lp-td lp-col-house">
                    <div class="lp-shimmer" style="width: 52px; height: 20px; border-radius: 6px;"></div>
                  </div>
                  <div class="lp-td lp-col-contact">
                    <div class="lp-shimmer" style="width: 80px; height: 12px; border-radius: 4px;"></div>
                  </div>
                  <div class="lp-td lp-col-active">
                    <div class="lp-shimmer" style="width: 60px; height: 12px; border-radius: 4px;"></div>
                  </div>
                  <div class="lp-td lp-col-actions">
                    <div class="lp-action-group">
                      <div class="lp-shimmer" style="width: 22px; height: 22px; border-radius: 6px;"></div>
                      <div class="lp-shimmer" style="width: 22px; height: 22px; border-radius: 6px;"></div>
                      <div class="lp-shimmer" style="width: 22px; height: 22px; border-radius: 6px;"></div>
                    </div>
                  </div>
                </div>

                <!-- Pagination row -->
                <div class="lp-pagination">
                  <div class="lp-show-rows">
                    Show rows
                    <span class="lp-rows-select">10 <v-icon size="11" color="#374151">mdi-chevron-down</v-icon></span>
                  </div>
                  <div class="lp-page-btns">
                    <span class="lp-page-info">1-4 of 4</span>
                    <div class="lp-page-btn lp-page-btn-disabled"><v-icon size="12"
                        color="#9ca3af">mdi-chevron-double-left</v-icon></div>
                    <div class="lp-page-btn lp-page-btn-disabled"><v-icon size="12"
                        color="#9ca3af">mdi-chevron-left</v-icon>
                    </div>
                    <div class="lp-page-btn lp-page-btn-active"
                      :style="`background:${theme_button_color || buttonColor}`">1</div>
                    <div class="lp-page-btn lp-page-btn-disabled"><v-icon size="12"
                        color="#9ca3af">mdi-chevron-right</v-icon>
                    </div>
                    <div class="lp-page-btn lp-page-btn-disabled"><v-icon size="12"
                        color="#9ca3af">mdi-chevron-double-right</v-icon></div>
                  </div>
                </div>
              </div>
              <!-- /table card -->

            </div>
            <!-- /lp-page-body -->

          </div>
          <!-- /lp-right -->

        </div>
        <!-- /lp-shell -->

      </div>
      <!-- /preview-section -->

      <v-row>

        <!-- ── Primary Color Picker ── -->
        <v-col cols="12" md="6">
          <div class="premium-panel">
            <div class="premium-panel-header">
              <div>
                <div class="premium-panel-title">Primary Color</div>
                <div class="premium-panel-sub">Used for sidebar accent, active states, and highlights</div>
              </div>
              <div class="color-dot" :style="`background:${theme_primary_color || primaryColor}`"></div>
            </div>
            <div class="premium-panel-body">
              <v-color-picker v-model="theme_primary_color" dot-size="22" hide-canvas show-swatches
                swatches-max-height="180" mode="hexa" hide-mode-switch flat width="100%" />
            </div>
          </div>
        </v-col>

        <!-- ── Button Color Picker ── -->
        <v-col cols="12" md="6">
          <div class="premium-panel">
            <div class="premium-panel-header">
              <div>
                <div class="premium-panel-title">Button Color</div>
                <div class="premium-panel-sub">Used for action buttons and secondary elements</div>
              </div>
              <div class="color-dot" :style="`background:${theme_button_color || buttonColor}`"></div>
            </div>
            <div class="premium-panel-body">
              <v-color-picker v-model="theme_button_color" dot-size="22" hide-canvas show-swatches
                swatches-max-height="180" mode="hexa" hide-mode-switch flat width="100%" />
            </div>
          </div>
        </v-col>

      </v-row>

  </div>

  <template #footer>
    <v-btn text class="btn-premium-cancel" @click="resetColors">Reset to Default</v-btn>
    <v-btn depressed :loading="loading" class="btn-premium-primary ml-3" @click="updateTheme">Submit</v-btn>
  </template>
  </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumPage from "../common/PremiumPage.vue";

export default {
  components: { PremiumPage },

  data() {
    return {
      loading: false,
      theme_primary_color: "",
      theme_button_color: "",
      serverSideErrors: [],
      user: null,
      imageError: false,
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL || "",
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("theme", ["setDarkMode", "setPrimaryColor", "setButtonColor"]),

    cleanHex(val) {
      if (!val) return "";
      return val.replace(/FF$/i, "").toUpperCase();
    },

    resetColors() {
      this.theme_primary_color = "#f87676";
      this.theme_button_color = "#30384d";
    },

    updateTheme() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        this.loading = true;
        this.$axios
          .put("/societyadmin/theme", {
            theme_primary_color: this.theme_primary_color,
            theme_button_color: this.theme_button_color,
          })
          .then((response) => {
            const data = response.data;
            this.loading = false;
            this.serverSideErrors = {};

            if (data.status) {
              let user = JSON.parse(localStorage.getItem("user") || "{}");
              user.theme_primary_color = this.theme_primary_color;
              user.theme_button_color = this.theme_button_color;
              localStorage.setItem("user", JSON.stringify(user));

              this.$vuetify.theme.themes.light.primary = this.theme_primary_color;
              this.$vuetify.theme.themes.dark.primary = this.theme_primary_color;
              this.$vuetify.theme.themes.light.secondary = this.theme_button_color;
              this.$vuetify.theme.themes.dark.secondary = this.theme_button_color;

              this.setPrimaryColor({ primaryColor: this.theme_primary_color });
              this.setButtonColor({ buttonColor: this.theme_button_color });
              document.documentElement.style.setProperty("--brand", this.theme_primary_color);
              document.documentElement.style.setProperty("--btn-color", this.theme_button_color);

              this.setAlert({ showAlert: true, message: data.message, variant: "green accent-4", timeout: 2000 });
            } else {
              if (!data.errors) {
                this.setAlert({ showAlert: true, message: data.message, variant: "red accent-3", timeout: 2000 });
              } else {
                for (let i = 0; i < data.errors.length; i++) {
                  this.serverSideErrors[data.errors[i].field] = data.errors[i].message;
                }
              }
            }
          });
      });
    },
  },

  mounted() {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    this.user = user;
    this.theme_primary_color = user.theme_primary_color || this.primaryColor || "#f87676";
    this.theme_button_color = user.theme_button_color || this.buttonColor || "#30384d";
  },
};
</script>

<style scoped>
/* ══════════════════════════════
   PAGE WRAPPER
══════════════════════════════ */
.theme-page-container {
  padding: 24px;
}

/* ══════════════════════════════
   COLOR PICKER PANELS
══════════════════════════════ */
.premium-panel {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.premium-panel-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.premium-panel-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--tx, #111827);
  letter-spacing: -0.01em;
}

.premium-panel-sub {
  font-size: 12px;
  color: var(--tx2, #6b7280);
  margin-top: 2px;
}

.premium-panel-body {
  padding: 20px;
  flex: 1;
}

.color-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  transition: background 0.25s ease;
}

/* v-color-picker overrides */
::v-deep .v-color-picker {
  box-shadow: none !important;
  background: transparent !important;
  width: 100% !important;
}

::v-deep .v-color-picker__sliders {
  padding: 0 0 8px 0 !important;
}

::v-deep .v-slider__track-container {
  height: 10px !important;
  border-radius: 8px !important;
  overflow: hidden;
}

::v-deep .v-slider__track-background,
::v-deep .v-slider__track-fill {
  border-radius: 8px !important;
}

::v-deep .v-slider__thumb {
  width: 18px !important;
  height: 18px !important;
  left: -9px !important;
  border: 2.5px solid #fff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25) !important;
  cursor: pointer;
}

::v-deep .v-slider__thumb::before {
  opacity: 0 !important;
}

::v-deep .v-color-picker__input input {
  border: 1.5px solid var(--border, #e2e8f0) !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  font-family: "Courier New", monospace !important;
  padding: 6px 10px !important;
  height: 36px !important;
  text-align: center;
  background: var(--surface2, #f9fafb) !important;
  color: var(--tx, #111827) !important;
  box-shadow: none !important;
  transition: border-color 0.2s;
}

::v-deep .v-color-picker__input input:focus {
  border-color: var(--brand) !important;
  outline: none;
}

::v-deep .v-color-picker__input span {
  font-size: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
  color: var(--tx3, #9ca3af) !important;
  text-transform: uppercase;
  margin-top: 4px !important;
}

::v-deep .v-color-picker__color {
  border-radius: 4px !important;
  height: 18px !important;
  width: 26px !important;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

::v-deep .v-color-picker__color:hover {
  transform: scale(1.15) !important;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2) !important;
  z-index: 1;
  position: relative;
}

/* ══════════════════════════════
   PREVIEW SECTION HEADER
══════════════════════════════ */
.preview-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 10px;
}

.preview-section-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: var(--tx, #111827);
  letter-spacing: -0.01em;
  margin-bottom: 2px;
}

.preview-section-sub {
  font-size: 12px;
  color: var(--tx2, #6b7280);
}

.preview-color-badges {
  display: flex;
  gap: 10px;
}

.preview-badge {
  display: flex;
  align-items: center;
  gap: 7px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 30px;
  padding: 5px 12px 5px 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.preview-badge-swatch {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  transition: background 0.25s ease;
}

.preview-badge-label {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.preview-badge-hex {
  font-family: "Courier New", monospace;
  font-size: 11px;
  font-weight: 700;
  color: #111827;
}

/* ══════════════════════════════
   PREVIEW SHELL
══════════════════════════════ */
.lp-shell {
  display: flex;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  height: auto;
  background: #fff;
}

/* ══════════════════════════════
   SIDEBAR  — matches your portal
══════════════════════════════ */
.lp-sidebar {
  width: 200px;
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.lp-logo-bar {
  height: 56px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 14px;
  flex-shrink: 0;
  transition: background 0.3s ease;
}

.lp-logo-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 13px;
  color: #333;
  flex-shrink: 0;
}

.lp-logo-name {
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  white-space: nowrap;
}

.lp-logo-sub {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.75);
}

.lp-nav-area {
  flex: 1;
  padding: 10px 0;
  overflow: hidden;
}

.lp-nav-section-label {
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ca3af;
  padding: 5px 14px 3px;
}

.lp-nav-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 14px 7px 17px;
  font-size: 11.5px;
  color: #4b5563;
  cursor: default;
  white-space: nowrap;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
}

.lp-nav-active {
  padding-left: 14px;
}

/* ══════════════════════════════
   RIGHT PANEL
══════════════════════════════ */
.lp-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  min-width: 0;
}

/* Topbar */
.lp-topbar {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
  transition: background 0.3s ease;
}

.lp-topbar-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}

.lp-topbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.lp-topbar-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.lp-bell-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ef4444;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
}

.lp-topbar-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  transition: background 0.3s ease;
}

/* Page body */
.lp-page-body {
  flex: none;
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Page header row */
.lp-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.lp-page-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.lp-page-sub {
  font-size: 11px;
  color: #6b7280;
  margin-top: 1px;
}

.lp-page-actions {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.lp-search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 5px 10px;
  min-width: 110px;
}

.lp-search-placeholder {
  font-size: 11px;
  color: #9ca3af;
}

.lp-filter-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 5px 9px;
  font-size: 11px;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
}

.lp-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
}

.lp-add-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  transition: background 0.3s ease;
}

/* Table card */
.lp-table-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  flex: none;
  display: flex;
  flex-direction: column;
}

/* Table head */
.lp-table-head {
  display: grid;
  grid-template-columns: 52px 1.8fr 1.2fr 1.5fr 1.2fr 110px;
  padding: 8px 14px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.lp-th {
  font-size: 9.5px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}

/* Table rows */
.lp-table-row {
  display: grid;
  grid-template-columns: 52px 1.8fr 1.2fr 1.5fr 1.2fr 110px;
  padding: 9px 14px;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
  transition: background 0.15s;
}

.lp-table-row:last-of-type {
  border-bottom: none;
}

.lp-table-row:hover {
  background: #f9fafb;
}

.lp-td {
  font-size: 11px;
  color: #374151;
  display: flex;
  align-items: center;
}

.lp-col-no {
  justify-content: center;
  color: #9ca3af;
  font-size: 10px;
}

.lp-col-name {
  gap: 8px;
}

.lp-col-house {}

.lp-col-contact {
  gap: 2px;
}

.lp-col-active {}

.lp-col-actions {
  gap: 2px;
}

.lp-member-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  transition: background 0.3s ease, color 0.3s ease;
}

.lp-member-name {
  font-size: 11px;
  font-weight: 600;
  color: #111827;
  line-height: 1.2;
}

.lp-no-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 4px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  background: #f1f5f9;
}

.lp-house-badge {
  display: inline-flex;
  align-items: center;
  gap: 3.5px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 6px;
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
}

.lp-action-group {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
}

.lp-action-btn {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.lp-action-delete {
  border-color: #fca5a5;
}

/* Pagination */
.lp-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-top: 1px solid #f3f4f6;
  background: #fafafa;
  flex-shrink: 0;
}

.lp-show-rows {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  color: #6b7280;
}

.lp-rows-select {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 600;
  color: #111827;
}

.lp-page-btns {
  display: flex;
  align-items: center;
  gap: 4px;
}

.lp-page-info {
  font-size: 10px;
  color: #6b7280;
  margin-right: 6px;
}

.lp-page-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.lp-page-btn-disabled {
  color: #9ca3af;
}

.lp-page-btn-active {
  color: #fff;
  border-color: transparent;
  transition: background 0.3s ease;
}

/* ── Shimmer skeleton ─────────────────── */
.lp-shimmer {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: lpShimmer 1.4s ease-in-out infinite;
  display: inline-block;
}

@keyframes lpShimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}
</style>